/**
 * Pure helpers for the admin Security Settings form.
 *
 * The form works directly with the snake_case `security_settings` API resource
 * (all values are strings). These helpers pick the managed subset, count enabled
 * complexity rules and validate ranges client-side — the service performs no
 * range validation of its own. Ranges/presets mirror the configuration-service
 * schema. Nothing here converts the data to a legacy shape.
 */

export const MAX_LOGIN_ATTEMPTS = 10;

// day_to_notify_before_expiry (first notification) accepts only these presets.
export const EXPIRY_NOTIFY_DAYS = [7, 14];
// day_to_remind_before_expiry (reminder) accepts 0..MAX_REMINDER_DAYS.
export const MAX_REMINDER_DAYS = 5;

// Keys the admin form is allowed to edit. The remaining security_settings keys
// (enforce_2fa, max_session_login_time, session_login_timeout) are intentionally
// omitted so the partial-merge PUT preserves their stored values.
export const MANAGED_SECURITY_SETTINGS_KEYS = [
    'amount_minimum_rules_requirements',
    'amount_password_store',
    'minimum_password_length',
    'require_one_upper_character',
    'require_one_lower_character',
    'require_one_number_character',
    'require_one_special_character',
    'days_until_expiry',
    'day_to_notify_before_expiry',
    'day_to_remind_before_expiry',
    'enable_expiry_notifications',
    'max_failed_login_attempts'
];

// Fallback shown before the real policy loads (snake_case strings, API shape).
export const DEFAULT_SECURITY_SETTINGS = {
    amount_minimum_rules_requirements: '4',
    amount_password_store: '0',
    minimum_password_length: '8',
    require_one_upper_character: 'true',
    require_one_lower_character: 'true',
    require_one_number_character: 'true',
    require_one_special_character: 'true',
    days_until_expiry: '90',
    day_to_notify_before_expiry: '7',
    day_to_remind_before_expiry: '0',
    enable_expiry_notifications: 'false',
    max_failed_login_attempts: String(MAX_LOGIN_ATTEMPTS)
};

const RULE_KEYS = [
    'require_one_upper_character',
    'require_one_lower_character',
    'require_one_number_character',
    'require_one_special_character'
];

const EXPIRY_MIN = 1;
const EXPIRY_MAX = 365;
const HISTORY_MAX = 30;
const LENGTH_MIN = 1;
const LENGTH_MAX = 100;

function getIsInRange(value, min, max) {
    const parsedNumber = parseInt(value, 10);
    return !Number.isNaN(parsedNumber) && parsedNumber >= min && parsedNumber <= max;
}

/**
 * Picks the keys the admin form manages, preserving the snake_case API shape.
 * @param {Record<string, string>} settings
 * @returns {Record<string, string>}
 */
export function getManagedSecuritySettings(settings = {}) {
    return MANAGED_SECURITY_SETTINGS_KEYS.reduce(
        (managed, key) => (settings[key] === undefined ? managed : {...managed, [key]: settings[key]}),
        {}
    );
}

// Keys the service validates as booleans. On PUT they must be sent as native
// booleans (a "true"/"false" string is rejected with HTTP 302); numeric keys
// are accepted as strings.
const BOOLEAN_SECURITY_SETTINGS_KEYS = [...RULE_KEYS, 'enable_expiry_notifications'];

/**
 * Counts how many password-complexity rules are switched on ("true").
 * @param {Record<string, string>} settings
 * @returns {number}
 */
export function getEnabledRulesCount(settings = {}) {
    return RULE_KEYS.filter((key) => settings[key] === 'true').length;
}

/**
 * Prepares the settings map for the PUT body: boolean keys become native
 * booleans (the service rejects them as strings), numeric keys stay as strings.
 * The stored state keeps the string API shape; only the wire payload is coerced.
 * @param {Record<string, string>} settings
 * @returns {Record<string, string | boolean>}
 */
export function getSerializedSecuritySettings(settings = {}) {
    return Object.fromEntries(
        Object.entries(settings).map(([key, value]) => [
            key,
            BOOLEAN_SECURITY_SETTINGS_KEYS.includes(key) ? value === 'true' : value
        ])
    );
}

function getClampedInt(value, min, max, fallback) {
    const parsedNumber = parseInt(value, 10);
    if (Number.isNaN(parsedNumber)) {
        return String(fallback);
    }
    return String(Math.min(max, Math.max(min, parsedNumber)));
}

/**
 * Coerces a loaded resource into in-range values so legacy/out-of-schema data
 * (e.g. an expiry of '0', a stale required-rules count, or a notify value that
 * is not a 7/14 preset) cannot render a blank select or lock the form via a
 * permanently-invalid field. Keys are left snake_case — this is not a legacy adapter.
 * @param {Record<string, string>} settings
 * @returns {Record<string, string>}
 */
export function getNormalizedSecuritySettings(settings = {}) {
    const enabledRulesCount = getEnabledRulesCount(settings);
    const isNotifyPreset = EXPIRY_NOTIFY_DAYS.includes(parseInt(settings.day_to_notify_before_expiry, 10));

    return {
        ...settings,
        days_until_expiry: getClampedInt(settings.days_until_expiry, EXPIRY_MIN, EXPIRY_MAX, EXPIRY_MIN),
        day_to_notify_before_expiry: isNotifyPreset
            ? settings.day_to_notify_before_expiry
            : String(EXPIRY_NOTIFY_DAYS[0]),
        day_to_remind_before_expiry: getClampedInt(settings.day_to_remind_before_expiry, 0, MAX_REMINDER_DAYS, 0),
        amount_password_store: getClampedInt(settings.amount_password_store, 0, HISTORY_MAX, 0),
        minimum_password_length: getClampedInt(settings.minimum_password_length, LENGTH_MIN, LENGTH_MAX, LENGTH_MIN),
        amount_minimum_rules_requirements: getClampedInt(settings.amount_minimum_rules_requirements, 0, enabledRulesCount, enabledRulesCount),
        max_failed_login_attempts: getClampedInt(settings.max_failed_login_attempts, 1, MAX_LOGIN_ATTEMPTS, MAX_LOGIN_ATTEMPTS)
    };
}

/**
 * Client-side range/preset validation, mirroring the configuration-service schema.
 * @param {string} name - snake_case field name
 * @param {string} value - string value from the API/form
 * @returns {boolean}
 */
export function getIsFieldValid(name, value) {
    switch (name) {
        case 'days_until_expiry':
            return getIsInRange(value, EXPIRY_MIN, EXPIRY_MAX);
        case 'day_to_notify_before_expiry':
            return EXPIRY_NOTIFY_DAYS.includes(parseInt(value, 10));
        case 'day_to_remind_before_expiry':
            return getIsInRange(value, 0, MAX_REMINDER_DAYS);
        case 'amount_password_store':
            return getIsInRange(value, 0, HISTORY_MAX);
        case 'minimum_password_length':
            return getIsInRange(value, LENGTH_MIN, LENGTH_MAX);
        default:
            return true;
    }
}
